import { TableCell, TableRow } from '@cbhq/cds-web/tables';
import { HStack, VStack } from '@cbhq/cds-web/layout';
import { TextBody, TextHeadline, TextLabel2 } from '@cbhq/cds-web/typography';
import { RemoteImage } from '@cbhq/cds-web/media';
import { memo, useCallback } from 'react';
import { DisplayType, format } from '@/utils/numberFormatter';
import type { TokenizedEquityData } from './hooks/useTokenizedEquitiesData';

type Props = {
  equity: TokenizedEquityData;
  onSelect?: (tokenAddress: string) => void;
};

// Column widths matching header (Token=25%, Stock Price=18%, Token Price=19%, Token Market Cap=19%, Token Volume=19%)
const COLUMN_WIDTHS = {
  token: '25%',
  stockPrice: '18%',
  tokenPrice: '19%',
  tokenMarketCap: '19%',
  tokenVolume: '19%',
};

export const TokenizedEquitiesRow = memo(function TokenizedEquitiesRow({
  equity,
  onSelect,
}: Props) {
  const imageUrl = equity.imageUrl;

  const isPositive = equity.stockPriceChange > 0;
  const changeColor = isPositive ? 'fgPositive' : 'fgNegative';
  const changeIcon = isPositive ? '↗' : '↘';

  const handleRowClick = useCallback(() => {
    onSelect?.(equity.tokenAddress);
  }, [onSelect, equity.tokenAddress]);

  return (
    <TableRow key={equity.id} onClick={handleRowClick} testID={`equity-row-${equity.symbol}`}>
      {/* Token Column */}
      <TableCell alignItems="flex-start" width={COLUMN_WIDTHS.token}>
        <HStack gap={1.5} alignItems="center">
          <RemoteImage source={imageUrl} size="l" shape="circle" />
          <VStack gap={0}>
            <TextHeadline as="span" noWrap>
              {equity.name}
            </TextHeadline>
            <TextBody as="span" color="fgMuted" noWrap>
              {equity.symbol}
            </TextBody>
          </VStack>
        </HStack>
      </TableCell>

      {/* Stock Price Column */}
      <TableCell alignItems="flex-start" width={COLUMN_WIDTHS.stockPrice}>
        <VStack gap={0} alignItems="flex-start">
          <TextBody as="span" noWrap>
            {format({ value: equity.stockPriceUsd, type: DisplayType.Fiat })}
          </TextBody>
          <TextLabel2 as="span" color={changeColor} noWrap>
            {changeIcon} {Math.abs(equity.stockPriceChange)}%
          </TextLabel2>
        </VStack>
      </TableCell>

      {/* Token Price Column */}
      <TableCell alignItems="flex-start" width={COLUMN_WIDTHS.tokenPrice}>
        <TextBody as="span" noWrap>
          {format({ value: equity.avgTokenPriceUsd, type: DisplayType.Fiat })}
        </TextBody>
      </TableCell>

      {/* Token Market Cap Column */}
      <TableCell alignItems="flex-start" width={COLUMN_WIDTHS.tokenMarketCap}>
        <TextBody as="span" noWrap>
          {format({ value: equity.tokenMarketCapUsd, type: DisplayType.FiatShort })}
        </TextBody>
      </TableCell>

      {/* Token Volume Column */}
      <TableCell alignItems="flex-start" width={COLUMN_WIDTHS.tokenVolume}>
        <TextBody as="span" noWrap>
          {format({ value: equity.tokenVolumeUsd, type: DisplayType.FiatShort })}
        </TextBody>
      </TableCell>
    </TableRow>
  );
});
